import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Bike,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Package,
  Check,
  X,
  Search,
  User,
  Car,
  Footprints,
  UserPlus,
  Truck,
} from "lucide-react";

const ZONAS = [
  { value: "norte", label: "Norte" },
  { value: "nororiente", label: "Nororiente" },
  { value: "noroccidente", label: "Noroccidente" },
  { value: "centro", label: "Centro" },
  { value: "chapinero", label: "Chapinero" },
  { value: "occidente", label: "Occidente" },
  { value: "sur_occidente", label: "Sur Occidente" },
  { value: "sur", label: "Sur" },
  { value: "soacha", label: "Soacha" },
  { value: "especial", label: "Especial" },
];

const TRANSPORTES = [
  { value: "moto", label: "Moto", icon: Bike },
  { value: "carro", label: "Carro", icon: Car },
  { value: "bicicleta", label: "Bicicleta", icon: Bike },
  { value: "caminando", label: "Caminando", icon: Footprints },
];

// Componente helper para mostrar icono de transporte
const TransporteIcon = ({ tipo }) => {
  const transporte = TRANSPORTES.find(t => t.value === tipo);
  const Icon = transporte?.icon || Bike;
  return <Icon className="w-4 h-4" />;
};

export default function AdminDomiciliarios() {
  const [domiciliarios, setDomiciliarios] = useState([]);
  const [vendedoresEntrega, setVendedoresEntrega] = useState([]);
  const [vendedoresSinEntrega, setVendedoresSinEntrega] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHabilitarDialog, setShowHabilitarDialog] = useState(false);
  const [editingDomiciliario, setEditingDomiciliario] = useState(null);
  const [habilitandoVendedor, setHabilitandoVendedor] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    password: "",
    documento: "",
    zonas_preferidas: [],
    medio_transporte: "moto",
    capacidad_pedidos: 20,
    notas_internas: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/despachos/domiciliarios");
      setDomiciliarios(res.data.domiciliarios || []);
      setVendedoresEntrega(res.data.vendedores_entrega || []);
      
      // Cargar vendedores SIN permiso de entrega para el diálogo de habilitar
      const usuariosRes = await api.get("/usuarios");
      const vendedoresSin = usuariosRes.data.filter(
        u => u.rol === "vendedor" && u.activo && !u.can_deliver_orders
      );
      setVendedoresSinEntrega(vendedoresSin);
    } catch (error) {
      console.error("Error fetching domiciliarios:", error);
      toast.error("Error al cargar domiciliarios");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.nombre || !formData.email || !formData.telefono || !formData.password) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    try {
      await api.post("/despachos/domiciliarios", formData);
      toast.success("Domiciliario creado exitosamente");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear domiciliario");
    }
  };

  const handleUpdate = async () => {
    if (!editingDomiciliario) return;

    try {
      const updateData = { ...formData };
      delete updateData.password;
      delete updateData.email;
      
      await api.put(`/despachos/domiciliarios/${editingDomiciliario.id}`, updateData);
      toast.success("Domiciliario actualizado");
      setEditingDomiciliario(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar domiciliario");
    }
  };

  const handleToggleActivo = async (domiciliario) => {
    try {
      await api.put(`/despachos/domiciliarios/${domiciliario.id}`, {
        activo: !domiciliario.activo
      });
      toast.success(domiciliario.activo ? "Domiciliario desactivado" : "Domiciliario activado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleToggleVendedorEntrega = async (vendedor, habilitar) => {
    try {
      // Primero obtener la configuración actual del vendedor
      const usuariosRes = await api.get("/usuarios");
      const vendedorActual = usuariosRes.data.find(u => u.id === vendedor.id);
      
      await api.put(`/usuarios/${vendedor.id}/config`, {
        apoyo_transporte: vendedorActual?.apoyo_transporte || 0,
        tipo_auxilio_transporte: vendedorActual?.tipo_auxilio_transporte || "periodico",
        otros_costos: vendedorActual?.otros_costos || 0,
        notas_config: vendedorActual?.notas_config || "",
        puede_registrar_gastos: vendedorActual?.puede_registrar_gastos || false,
        can_deliver_orders: habilitar
      });
      
      toast.success(
        habilitar 
          ? `${vendedor.nombre} ahora puede hacer entregas` 
          : `${vendedor.nombre} ya no puede hacer entregas`
      );
      setShowHabilitarDialog(false);
      setHabilitandoVendedor(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar permisos");
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: "",
      email: "",
      telefono: "",
      password: "",
      documento: "",
      zonas_preferidas: [],
      medio_transporte: "moto",
      capacidad_pedidos: 20,
      notas_internas: "",
    });
  };

  const openEditDialog = (domiciliario) => {
    setFormData({
      nombre: domiciliario.nombre || "",
      email: domiciliario.email || "",
      telefono: domiciliario.telefono || "",
      password: "",
      documento: domiciliario.documento || "",
      zonas_preferidas: domiciliario.zonas_preferidas || [],
      medio_transporte: domiciliario.medio_transporte || "moto",
      capacidad_pedidos: domiciliario.capacidad_pedidos || 20,
      notas_internas: domiciliario.notas_internas || "",
    });
    setEditingDomiciliario(domiciliario);
  };

  const filteredDomiciliarios = domiciliarios.filter(d => 
    d.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.telefono?.includes(searchTerm)
  );

  const filteredVendedores = vendedoresEntrega.filter(v =>
    v.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C8902A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-domiciliarios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
            Domiciliarios
          </h1>
          <p className="text-[#4A2C0A]/60 font-['Poppins'] text-sm">
            Gestiona el equipo de entregas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowHabilitarDialog(true)}
            variant="outline"
            className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <UserPlus className="w-4 h-4" />
            Habilitar Vendedor
          </Button>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="gap-2 bg-[#C8902A] hover:bg-[#A67720]"
          >
            <Plus className="w-4 h-4" />
            Nuevo Domiciliario
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C0A]/40" />
        <Input
          placeholder="Buscar por nombre, email o teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-[#E5D4B3]"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
          <p className="text-xs text-[#4A2C0A]/60">Domiciliarios</p>
          <p className="text-2xl font-bold text-[#4A2C0A]">{domiciliarios.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-green-200">
          <p className="text-xs text-green-600">Activos</p>
          <p className="text-2xl font-bold text-green-600">
            {domiciliarios.filter(d => d.activo).length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-blue-200">
          <p className="text-xs text-blue-600">Vendedores con Entrega</p>
          <p className="text-2xl font-bold text-blue-600">{vendedoresEntrega.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#C8902A]/30">
          <p className="text-xs text-[#C8902A]">Capacidad Total</p>
          <p className="text-2xl font-bold text-[#C8902A]">
            {domiciliarios.reduce((sum, d) => sum + (d.capacidad_pedidos || 20), 0)} pedidos
          </p>
        </div>
      </div>

      {/* Domiciliarios List */}
      <div className="space-y-4">
        <h2 className="font-semibold text-[#4A2C0A]">Domiciliarios ({filteredDomiciliarios.length})</h2>
        
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredDomiciliarios.map((dom) => (
            <div
              key={dom.id}
              className={`bg-white rounded-xl border-2 p-4 transition-all ${
                dom.activo ? "border-[#E5D4B3] hover:border-[#C8902A]/50" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    dom.activo ? "bg-[#C8902A]/10" : "bg-gray-200"
                  }`}>
                    <Bike className={`w-5 h-5 ${dom.activo ? "text-[#C8902A]" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${dom.activo ? "text-[#4A2C0A]" : "text-gray-500"}`}>
                      {dom.nombre}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      dom.activo ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"
                    }`}>
                      {dom.activo ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(dom)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="w-4 h-4 text-[#4A2C0A]/60" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActivo(dom)}
                    className="h-8 w-8 p-0"
                  >
                    {dom.activo ? (
                      <X className="w-4 h-4 text-red-500" />
                    ) : (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                  <Phone className="w-3 h-3" />
                  <span>{dom.telefono}</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                  <Mail className="w-3 h-3" />
                  <span className="truncate">{dom.email}</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                  <TransporteIcon tipo={dom.medio_transporte} />
                  <span className="capitalize">{dom.medio_transporte || "Moto"}</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                  <Package className="w-3 h-3" />
                  <span>Capacidad: {dom.capacidad_pedidos || 20} pedidos</span>
                </div>
              </div>

              {dom.zonas_preferidas?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {dom.zonas_preferidas.map((zona) => (
                    <span
                      key={zona}
                      className="text-xs px-2 py-0.5 bg-[#FBF5E8] text-[#4A2C0A] rounded-full"
                    >
                      {ZONAS.find(z => z.value === zona)?.label || zona}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredDomiciliarios.length === 0 && (
          <div className="text-center py-8 bg-white rounded-xl border border-[#E5D4B3]">
            <Bike className="w-12 h-12 mx-auto mb-3 text-[#E5D4B3]" />
            <p className="text-[#4A2C0A]">No hay domiciliarios registrados</p>
          </div>
        )}
      </div>

      {/* Vendedores con Entrega */}
      {vendedoresEntrega.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-[#4A2C0A]">
            Vendedores con Entrega Habilitada ({filteredVendedores.length})
          </h2>
          
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredVendedores.map((ven) => (
              <div
                key={ven.id}
                className="bg-white rounded-xl border-2 border-blue-200 p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#4A2C0A]">{ven.nombre}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Vendedor + Entrega
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleVendedorEntrega(ven, false)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Quitar permiso de entrega"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                    <Phone className="w-3 h-3" />
                    <span>{ven.telefono || "Sin teléfono"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{ven.email}</span>
                  </div>
                  {ven.zona && (
                    <div className="flex items-center gap-2 text-[#4A2C0A]/70">
                      <MapPin className="w-3 h-3" />
                      <span>{ven.zona}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={showCreateDialog || !!editingDomiciliario}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingDomiciliario(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">
              {editingDomiciliario ? "Editar Domiciliario" : "Nuevo Domiciliario"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#4A2C0A]">Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre completo"
                  className="border-[#E5D4B3]"
                />
              </div>
              <div>
                <Label className="text-[#4A2C0A]">Teléfono *</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="3001234567"
                  className="border-[#E5D4B3]"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className="border-[#E5D4B3]"
                disabled={!!editingDomiciliario}
              />
            </div>

            {!editingDomiciliario && (
              <div>
                <Label className="text-[#4A2C0A]">Contraseña *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  className="border-[#E5D4B3]"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#4A2C0A]">Documento</Label>
                <Input
                  value={formData.documento}
                  onChange={(e) => setFormData({ ...formData, documento: e.target.value })}
                  placeholder="Cédula"
                  className="border-[#E5D4B3]"
                />
              </div>
              <div>
                <Label className="text-[#4A2C0A]">Medio de Transporte</Label>
                <Select
                  value={formData.medio_transporte}
                  onValueChange={(value) => setFormData({ ...formData, medio_transporte: value })}
                >
                  <SelectTrigger className="border-[#E5D4B3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSPORTES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Capacidad de Pedidos por Ruta</Label>
              <Input
                type="number"
                value={formData.capacidad_pedidos}
                onChange={(e) => setFormData({ ...formData, capacidad_pedidos: parseInt(e.target.value) || 20 })}
                min={1}
                max={100}
                className="border-[#E5D4B3]"
              />
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Zonas Preferidas</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ZONAS.map((zona) => (
                  <button
                    key={zona.value}
                    type="button"
                    onClick={() => {
                      const current = formData.zonas_preferidas || [];
                      const updated = current.includes(zona.value)
                        ? current.filter(z => z !== zona.value)
                        : [...current, zona.value];
                      setFormData({ ...formData, zonas_preferidas: updated });
                    }}
                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                      formData.zonas_preferidas?.includes(zona.value)
                        ? "bg-[#C8902A] text-white border-[#C8902A]"
                        : "bg-white text-[#4A2C0A] border-[#E5D4B3] hover:border-[#C8902A]"
                    }`}
                  >
                    {zona.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[#4A2C0A]">Notas Internas</Label>
              <Input
                value={formData.notas_internas}
                onChange={(e) => setFormData({ ...formData, notas_internas: e.target.value })}
                placeholder="Observaciones internas..."
                className="border-[#E5D4B3]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setEditingDomiciliario(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={editingDomiciliario ? handleUpdate : handleCreate}
              className="bg-[#C8902A] hover:bg-[#A67720]"
            >
              {editingDomiciliario ? "Guardar Cambios" : "Crear Domiciliario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Habilitar Vendedor Dialog */}
      <Dialog open={showHabilitarDialog} onOpenChange={setShowHabilitarDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Habilitar Vendedor para Entregas
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-[#4A2C0A]/70 mb-4">
              Selecciona un vendedor existente para habilitarle el permiso de hacer entregas. 
              Esto le permitirá acceder al módulo de entregas y recibir rutas asignadas.
            </p>

            {vendedoresSinEntrega.length === 0 ? (
              <div className="text-center py-8 bg-[#FBF5E8] rounded-xl">
                <User className="w-12 h-12 mx-auto mb-3 text-[#C8902A]/50" />
                <p className="text-[#4A2C0A] font-medium">Todos los vendedores ya tienen permiso</p>
                <p className="text-sm text-[#4A2C0A]/60 mt-1">
                  No hay vendedores activos pendientes por habilitar
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {vendedoresSinEntrega.map((vendedor) => (
                  <div
                    key={vendedor.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-[#E5D4B3] hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#FBF5E8] flex items-center justify-center">
                        {vendedor.foto_url ? (
                          <img src={vendedor.foto_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-[#C8902A]" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#4A2C0A]">{vendedor.nombre}</p>
                        <p className="text-xs text-[#4A2C0A]/60">
                          {vendedor.zona || "Sin zona"} • {vendedor.tipo_vendedor === "digital" ? "Digital" : "Carrito"}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleToggleVendedorEntrega(vendedor, true)}
                      className="gap-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Truck className="w-4 h-4" />
                      Habilitar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowHabilitarDialog(false)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
